import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Repeat, Pause, Play, Trash2 } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { recurringRulesResponse } from '../lib/types'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { describeSchedule, scheduleToState, stateToSchedule } from './recurrenceSchedule'
import { todayKey, weekStartJsDay } from '../lib/format'

// JS getDay(): 0 = Sunday … 6 = Saturday — the values the backend `byWeekday`
// uses. Displayed starting on the person's first day of the week (orderedWeekdays).
const WEEKDAYS = [
  { v: 0, label: 'S', full: 'Sun' },
  { v: 1, label: 'M', full: 'Mon' },
  { v: 2, label: 'T', full: 'Tue' },
  { v: 3, label: 'W', full: 'Wed' },
  { v: 4, label: 'T', full: 'Thu' },
  { v: 5, label: 'F', full: 'Fri' },
  { v: 6, label: 'S', full: 'Sat' },
]

/** WEEKDAYS rotated to start on the person's first day of the week. */
const orderedWeekdays = () => {
  const first = weekStartJsDay()
  return [...WEEKDAYS.slice(first), ...WEEKDAYS.slice(0, first)]
}

// Each preset carries a one-line hint (shown on hover) so the terse label
// isn't the only clue to what it does.
const PRESETS = [
  // "Daily" was removed — "Weekdays" already covers the every-working-day case.
  // Existing daily rules still fire (the engine keeps `daily`) and load into the
  // Custom builder when edited.
  { key: 'weekdays', label: 'Weekdays', hint: 'Every working day in your company’s working week' },
  { key: 'weekly', label: 'Weekly', hint: 'Once a week on the day(s) you pick' },
  { key: 'biweekly', label: 'Every 2 weeks', hint: 'Once every two weeks' },
  { key: 'monthly', label: 'Monthly', hint: 'Once a month on a chosen date' },
  { key: 'custom', label: 'Custom', hint: 'Set your own interval and days' },
]

/**
 * Repeat / Recurrence editor for a task. Attaches a RecurringTaskRule to the
 * task (the "template") via the existing `/tasks/:taskId/recurring` API; the
 * backend schedule-generates one occurrence at a time. Supports create, edit,
 * pause/resume and stop. One rule per task in this UI (the common case).
 */
export function RecurrenceDialog({ open, taskId, taskName, onClose }) {
  const qc = useQueryClient()
  const rulesQuery = useQuery({
    queryKey: ['recurring', taskId],
    enabled: open && Boolean(taskId),
    queryFn: async () => {
      const res = await api.get(`/tasks/${taskId}/recurring`)
      return recurringRulesResponse.parse(res.data)
    },
  })
  const rule = rulesQuery.data?.data?.[0] ?? null

  const [st, setSt] = useState(() => scheduleToState(null))
  const [endMode, setEndMode] = useState('never')
  const [endDate, setEndDate] = useState('')
  const [maxOccurrences, setMaxOccurrences] = useState('')

  // Load the existing rule into the form when it arrives / dialog re-opens.
  useEffect(() => {
    if (!open) return
    setSt(scheduleToState(rule))
    if (rule?.endDate) {
      setEndMode('date')
      setEndDate(String(rule.endDate).slice(0, 10))
    } else if (rule?.maxOccurrences) {
      setEndMode('count')
      setMaxOccurrences(String(rule.maxOccurrences))
    } else {
      setEndMode('never')
      setEndDate('')
      setMaxOccurrences('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rule?.id])

  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  )

  function buildPayload() {
    return {
      schedule: stateToSchedule(st),
      timezone,
      endDate: endMode === 'date' && endDate ? endDate : null,
      maxOccurrences:
        endMode === 'count' && Number(maxOccurrences) > 0
          ? Number(maxOccurrences)
          : null,
    }
  }

  // "End after N occurrences" must be a whole number ≥ 1. Previously a negative
  // or empty value was silently coerced to null (endless) and the save still
  // reported success — now it's blocked with an inline error.
  const countInvalid =
    endMode === 'count' &&
    !(Number.isInteger(Number(maxOccurrences)) && Number(maxOccurrences) >= 1)

  // "Stop repeating on" must not be in the past. A past end date saved fine,
  // fired once, then silently marked the rule exhausted with nothing shown to
  // the user (defect DF_016 / Mantis 22144). Mirrors the occurrence-count rule
  // above; the API enforces the same bound.
  //
  // Note this is deliberately NOT the task Start/Due rule — those accept past
  // dates on purpose (back-dating work you already did). An end date is an
  // instruction about FUTURE occurrences, so a past one is never meaningful.
  const todayIso = todayKey() // YYYY-MM-DD in the person's display zone
  const endDateInvalid = endMode === 'date' && Boolean(endDate) && endDate < todayIso

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['recurring', taskId] })
    qc.invalidateQueries({ queryKey: ['tasks'] })
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (rule) {
        const res = await api.patch(`/recurring-rules/${rule.id}`, buildPayload())
        return res.data
      }
      const res = await api.post(`/tasks/${taskId}/recurring`, buildPayload())
      return res.data
    },
    onSuccess: () => {
      invalidate()
      toast.success(rule ? 'Recurrence updated.' : 'Recurrence set.')
      onClose?.()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const toggleMutation = useMutation({
    mutationFn: async () =>
      api.patch(`/recurring-rules/${rule.id}`, { isActive: !rule.isActive }),
    onSuccess: () => {
      invalidate()
      toast.success(rule.isActive ? 'Recurrence paused.' : 'Recurrence resumed.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const stopMutation = useMutation({
    mutationFn: async () => api.delete(`/recurring-rules/${rule.id}`),
    onSuccess: () => {
      invalidate()
      toast.success('Recurrence stopped.')
      onClose?.()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const showWeekdays = st.preset === 'weekly' || st.preset === 'biweekly' ||
    (st.preset === 'custom' && st.freq === 'weekly')
  const showInterval = st.preset === 'custom'

  const chip = (active) =>
    'h-8 min-w-8 px-2 rounded-md text-xs font-semibold border transition-colors cursor-pointer ' +
    (active
      ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]'
      : 'bg-[color:var(--color-card)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)] hover:text-[color:var(--color-ink-strong)]')

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Opened from INSIDE the Task detail dialog (its portal sits at z-50), so
      // this must render ABOVE it — otherwise the detail dialog's backdrop
      // covers this modal and swallows every click (presets, Ends, buttons).
      overlayZClass="z-[80]"
      title={
        <span className="inline-flex items-center gap-2">
          <Repeat size={16} className="text-[color:var(--color-accent)]" /> Repeat
        </span>
      }
      footer={
        <>
          {rule ? (
            <button
              type="button"
              onClick={() => stopMutation.mutate()}
              disabled={stopMutation.isPending}
              className="mr-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <Trash2 size={14} /> Stop Repeating
            </button>
          ) : null}
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={countInvalid || endDateInvalid}
            type="button"
          >
            {rule ? 'Save' : 'Set Repeat'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Make <span className="font-medium text-[color:var(--color-ink)]">{taskName}</span> repeat
          on a schedule. Each time it comes due, a fresh copy is created for you — with the same
          people and subtasks — so you never have to add it again.
        </p>

        {/* Pattern presets */}
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() =>
                setSt((v) => ({
                  ...v,
                  preset: p.key,
                  // Entering "Custom" from a preset with no useful default (its
                  // freq is still 'daily') → default to weekly so the interval
                  // row AND the weekday picker appear straight away, instead of
                  // a bare "Every 1 day(s)" that looks like nothing changed.
                  freq: p.key === 'custom' && v.freq === 'daily' ? 'weekly' : v.freq,
                }))
              }
              className={chip(st.preset === p.key)}
              title={p.hint}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Plain-English summary of the current choice — updates live as the
         *  preset / days / interval change, so the terse chip labels are never
         *  the only clue to what will happen. */}
        <p className="rounded-lg bg-[color:var(--color-accent-bg)]/50 px-3 py-2 text-sm text-[color:var(--color-ink)]">
          {describeSchedule(st)}
        </p>

        {/* Custom builder — a clearly-bordered panel so selecting Custom always
         *  reveals editable controls. */}
        {showInterval ? (
          <div className="rounded-lg border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)]/40 p-3 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[color:var(--color-ink-muted)]">Repeat every</span>
              <input
                type="number"
                min="1"
                value={st.interval}
                onChange={(e) => setSt((v) => ({ ...v, interval: Math.max(1, Number(e.target.value) || 1) }))}
                className="h-9 w-16 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-center text-sm text-[color:var(--color-ink)]"
              />
              <select
                value={st.freq}
                onChange={(e) => setSt((v) => ({ ...v, freq: e.target.value }))}
                className="h-9 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-2 text-sm text-[color:var(--color-ink)] cursor-pointer"
              >
                <option value="daily">day(s)</option>
                <option value="weekly">week(s)</option>
                <option value="monthly">month(s)</option>
              </select>
            </div>
          </div>
        ) : null}

        {/* Weekday chips */}
        {showWeekdays ? (
          <div>
            <p className="mb-1.5 text-xs font-medium text-[color:var(--color-ink-muted)]">On these days</p>
            <div className="flex gap-1.5">
              {orderedWeekdays().map((d) => {
                const on = st.weekdays.includes(d.v)
                return (
                  <button
                    key={d.v}
                    type="button"
                    title={d.full}
                    onClick={() =>
                      setSt((v) => {
                        const set = new Set(v.weekdays)
                        if (set.has(d.v)) set.delete(d.v)
                        else set.add(d.v)
                        const next = [...set].sort((a, b) => a - b)
                        return { ...v, weekdays: next.length ? next : v.weekdays }
                      })
                    }
                    className={chip(on)}
                  >
                    {d.label}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}

        {/* Monthly always fires on the last day of the month (matches Report
         *  Settings), so there is no day-of-month picker — the live summary
         *  above states the behaviour. */}

        {/* Ends */}
        <div>
          <p className="mb-1.5 text-xs font-medium text-[color:var(--color-ink-muted)]">Ends</p>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {[
              { k: 'never', label: 'Never' },
              { k: 'date', label: 'On date' },
              { k: 'count', label: 'After' },
            ].map((o) => (
              <button
                key={o.k}
                type="button"
                onClick={() => setEndMode(o.k)}
                className={chip(endMode === o.k)}
              >
                {o.label}
              </button>
            ))}
            {endMode === 'date' ? (
              <input
                type="date"
                // `min` stops the picker offering a past day at all; the
                // explicit check still runs because a typed date bypasses it.
                min={todayIso}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                aria-invalid={endDateInvalid || undefined}
                className={
                  'h-9 rounded-md border bg-[color:var(--color-card)] px-2 text-sm text-[color:var(--color-ink)] ' +
                  (endDateInvalid
                    ? 'border-[color:var(--color-priority-urgent)]'
                    : 'border-[color:var(--color-rule)]')
                }
              />
            ) : null}
            {endMode === 'count' ? (
              <span className="inline-flex items-center gap-1.5">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={maxOccurrences}
                  onChange={(e) => setMaxOccurrences(e.target.value)}
                  placeholder="10"
                  aria-invalid={countInvalid || undefined}
                  className={
                    'h-9 w-16 rounded-md border bg-[color:var(--color-card)] px-2 text-center text-sm text-[color:var(--color-ink)] ' +
                    (countInvalid
                      ? 'border-[color:var(--color-priority-urgent)]'
                      : 'border-[color:var(--color-rule)]')
                  }
                />
                <span className="text-[color:var(--color-ink-muted)]">occurrences</span>
              </span>
            ) : null}
            {countInvalid ? (
              <p role="alert" className="w-full text-xs text-[color:var(--color-priority-urgent)]">
                Enter a whole number of occurrences greater than 0.
              </p>
            ) : null}
            {endDateInvalid ? (
              <p role="alert" className="w-full text-xs text-[color:var(--color-priority-urgent)]">
                The end date cannot be in the past.
              </p>
            ) : null}
          </div>
        </div>

        {/* Existing-rule status + pause/resume */}
        {rule ? (
          <div className="flex items-center justify-between rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-xs">
            <span className="text-[color:var(--color-ink-muted)]">
              {rule.isActive ? 'Active' : 'Paused'}
              {typeof rule.instancesCreated === 'number'
                ? ` · ${rule.instancesCreated} created`
                : ''}
              {rule.nextRunAt && rule.isActive
                ? ` · next ${String(rule.nextRunAt).slice(0, 10)}`
                : ''}
            </span>
            <button
              type="button"
              onClick={() => toggleMutation.mutate()}
              disabled={toggleMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              {rule.isActive ? (
                <>
                  <Pause size={13} /> Pause
                </>
              ) : (
                <>
                  <Play size={13} /> Resume
                </>
              )}
            </button>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
